import React, { useMemo } from 'react';
import { SquareIcon, PauseIcon, PlayIcon, UsersIcon, ListChecksIcon, Loader2Icon, CheckCircle2Icon, CircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
import { useI18n } from '../../config/i18n';
interface TimedParticipant {
  appearsAt: number;
  name: string;
  role: string;
  status?: 'active' | 'weak';
}
interface TopicItem {
  content: string;
  coveredAt: number | null;
}
function buildParticipants(crewRoles: {
  name: string;
  role: string;
}[]): TimedParticipant[] {
  return crewRoles.map((cr, i) => ({
    appearsAt: 6 + i * 12,
    name: cr.name,
    role: cr.role,
    status: (i === crewRoles.length - 1 ? 'weak' : 'active') as 'active' | 'weak'
  }));
}
function buildTopics(data: any): TopicItem[] {
  // Derive topics from actual step hazards and general hazards
  const stepTopics = (data.stepHazardData || []).slice(0, 4).map((step: any, i: number) => ({
    content: step.description || `Step ${i + 1}`,
    coveredAt: 18 + i * 14
  }));
  const uncoveredTopics = [{
    content: 'PPE confirmation for full crew',
    coveredAt: null
  }, {
    content: 'Emergency stop procedures',
    coveredAt: null
  }];
  const generalTopics = (data.generalHazards || []).slice(0, 2).map((h: any, i: number) => ({
    content: h.hazard || 'General hazard',
    coveredAt: null
  }));
  return [...uncoveredTopics, ...stepTopics, ...generalTopics];
}
const AI_NOTE_APPEARS_AT = 70;
interface RecordingScreenProps extends ScreenProps {
  recordingSeconds: number;
  isRecordingPaused: boolean;
  onTogglePause: () => void;
}
export function Screen12_Recording({
  data,
  onNext,
  recordingSeconds,
  isRecordingPaused,
  onTogglePause
}: RecordingScreenProps) {
  const {
    t
  } = useI18n();
  const PARTICIPANTS = useMemo(() => buildParticipants(data.crewRoles), [data.crewRoles]);
  const ALL_TOPICS = useMemo(() => buildTopics(data), [data]);
  const seconds = recordingSeconds;
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };
  const visibleParticipants = useMemo(() => PARTICIPANTS.filter(p => seconds >= p.appearsAt), [seconds]);
  const sortedTopics = useMemo(() => {
    return [...ALL_TOPICS].sort((a, b) => {
      const aCovered = a.coveredAt !== null && seconds >= a.coveredAt;
      const bCovered = b.coveredAt !== null && seconds >= b.coveredAt;
      if (aCovered === bCovered) return 0;
      return aCovered ? 1 : -1;
    });
  }, [seconds]);
  const coveredCount = useMemo(() => ALL_TOPICS.filter(t => t.coveredAt !== null && seconds >= t.coveredAt).length, [seconds]);
  const showAINote = seconds >= AI_NOTE_APPEARS_AT;
  const analysisStarted = seconds >= 3;
  return <div className="flex flex-col h-full" data-id="element-929">
      <div className="mb-5 flex flex-col items-center text-center" data-id="element-930">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 ${isRecordingPaused ? 'bg-amber-100 text-amber-500' : 'bg-red-100 text-red-500'}`} data-id="element-931">
          {isRecordingPaused ? <PauseIcon className="w-2.5 h-2.5 fill-current" data-id="element-932" /> : <motion.div animate={{
          opacity: [1, 0.3, 1]
        }} transition={{
          repeat: Infinity,
          duration: 1.5
        }} className="w-2 h-2 rounded-full bg-red-500" data-id="element-933" />}
          {isRecordingPaused ? 'Paused' : 'Recording Live'}
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-934">
          Recording PTP Meeting
        </h2>
        <div className="flex items-center gap-4 mb-2" data-id="element-935">
          <div className="flex items-center justify-center gap-[3px] h-8 w-16" data-id="element-936">
            {Array.from({
            length: 12
          }).map((_, i) => <motion.div key={i} animate={isRecordingPaused ? {
            height: '20%'
          } : {
            height: ['20%', `${Math.random() * 80 + 20}%`, '20%']
          }} transition={isRecordingPaused ? {
            duration: 0.3
          } : {
            repeat: Infinity,
            duration: 0.4 + Math.random() * 0.4,
            ease: 'easeInOut'
          }} className={`w-[3px] rounded-full ${isRecordingPaused ? 'bg-amber-300' : 'bg-yellow-500'}`} data-id="element-937" />)}
          </div>
          <div className={`text-4xl font-mono font-light tracking-tight tabular-nums ${isRecordingPaused ? 'text-amber-500' : 'text-gray-900'}`} data-id="element-938">
            {formatTime(seconds)}
          </div>
          <div className="flex items-center justify-center gap-[3px] h-8 w-16" data-id="element-939">
            {Array.from({
            length: 12
          }).map((_, i) => <motion.div key={i} animate={isRecordingPaused ? {
            height: '20%'
          } : {
            height: ['20%', `${Math.random() * 80 + 20}%`, '20%']
          }} transition={isRecordingPaused ? {
            duration: 0.3
          } : {
            repeat: Infinity,
            duration: 0.4 + Math.random() * 0.4,
            ease: 'easeInOut'
          }} className={`w-[3px] rounded-full ${isRecordingPaused ? 'bg-amber-300' : 'bg-yellow-500'}`} data-id="element-940" />)}
          </div>
        </div>
        <p className="text-gray-400 text-xs" data-id="element-941">
          {isRecordingPaused ? 'Recording paused. Tap Resume to continue.' : 'AI is analyzing the meeting in real time.'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-3" data-id="element-942">
        {!analysisStarted && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-8 flex flex-col items-center justify-center text-center" data-id="element-943">
            <Loader2Icon className="w-8 h-8 text-gray-400 mb-3 animate-spin" data-id="element-944" />
            <p className="text-gray-400 text-sm font-medium" data-id="element-945">
              {t('recording.listening')}
            </p>
            <p className="text-gray-400 text-xs mt-1" data-id="element-946">
              AI analysis will appear as the meeting progresses
            </p>
          </motion.div>}

        <AnimatePresence data-id="element-947">
          {showAINote && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-id="element-948">
              <p className="text-yellow-700 text-sm leading-snug" data-id="element-949">
                <span className="font-bold" data-id="element-950">AI Note:</span> Meeting is active.
                Coverage is incomplete. Continue discussing hazards, controls,
                and crew responsibilities.
              </p>
            </motion.div>}
        </AnimatePresence>

        {/* Detected Participants — minimal tags */}
        {analysisStarted && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-951">
            <div className="flex items-center gap-2 mb-2" data-id="element-952">
              <UsersIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-953" />
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-954">
                Detected ({visibleParticipants.length}/{PARTICIPANTS.length})
              </h4>
              {visibleParticipants.length < PARTICIPANTS.length && <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 1.2,
            ease: 'linear'
          }} className="ml-auto" data-id="element-955">
                  <Loader2Icon className="w-3 h-3 text-gray-400" data-id="element-956" />
                </motion.div>}
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-957">
              <AnimatePresence data-id="element-958">
                {visibleParticipants.map(p => <motion.span key={p.name} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.25
            }} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border ${p.status === 'weak' ? 'bg-white border-gray-200 text-gray-400' : 'bg-white border-gray-200 text-gray-700'}`} data-id="element-959">
                    <span className="font-semibold" data-id="element-960">
                      {p.name.split(' ')[0]}
                    </span>
                    <span className="text-gray-400" data-id="element-961">·</span>
                    <span className="text-gray-400" data-id="element-962">{p.role}</span>
                  </motion.span>)}
              </AnimatePresence>
            </div>
          </motion.div>}

        {/* Topic Coverage */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-963">
          <div className="flex items-center justify-between mb-3" data-id="element-964">
            <div className="flex items-center gap-2" data-id="element-965">
              <ListChecksIcon className="w-4 h-4 text-gray-500" data-id="element-966" />
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-967">
                Topic Coverage
              </h4>
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-968">
              {coveredCount}/{ALL_TOPICS.length}
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4" data-id="element-969">
            <motion.div className="bg-green-500 h-1.5 rounded-full" animate={{
            width: `${coveredCount / ALL_TOPICS.length * 100}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-970" />
          </div>

          <ul className="space-y-1.5" data-id="element-971">
            <AnimatePresence data-id="element-972">
              {sortedTopics.map(topic => {
              const isCovered = topic.coveredAt !== null && seconds >= topic.coveredAt;
              return <motion.li key={topic.content} layout transition={{
                layout: {
                  duration: 0.4,
                  ease: 'easeInOut'
                }
              }} className={`flex items-center gap-2.5 py-1.5 px-2 rounded-lg text-sm transition-colors ${isCovered ? 'bg-green-50' : 'bg-amber-50'}`} data-id="element-973">
                    {isCovered ? <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 15
                }} data-id="element-974">
                        <CheckCircle2Icon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-975" />
                      </motion.div> : <CircleIcon className="w-4 h-4 text-amber-400 flex-shrink-0" data-id="element-976" />}
                    <span className={isCovered ? 'text-green-600 line-through decoration-green-300 text-xs' : 'text-amber-700 text-xs'} data-id="element-977">
                      {topic.content}
                    </span>
                    {!isCovered && <span className="ml-auto text-[9px] font-bold text-amber-500 uppercase tracking-wider flex-shrink-0" data-id="element-978">
                        Missing
                      </span>}
                  </motion.li>;
            })}
            </AnimatePresence>
          </ul>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-979">
        <PrimaryButton onClick={onNext} className="bg-red-500 hover:bg-red-600 text-white shadow-red-500/20" icon={<SquareIcon className="w-5 h-5 fill-current" data-id="element-981" />} data-id="element-980">
          Stop Recording
        </PrimaryButton>
        <SecondaryButton onClick={onTogglePause} icon={isRecordingPaused ? <PlayIcon className="w-5 h-5 fill-current" data-id="element-983" /> : <PauseIcon className="w-5 h-5 fill-current" data-id="element-984" />} data-id="element-982">
          {isRecordingPaused ? 'Resume' : 'Pause'}
        </SecondaryButton>
      </div>
    </div>;
}